import { forwardRef } from "react";
import { Bell, BellOff, FolderKanban, GripVertical, ListChecks, MessageCircleQuestion } from "lucide-react";
import { spaceOf } from "@/lib/spaces";
import { daysLeft, fmtDate, fmtDay, fromNow, projectProgress } from "@/lib/time";
import { priorityOf, scheduleText } from "@/lib/priority";
import { ProgressBar } from "@/components/ProjectCard";
import { overdueSince } from "@/components/wallboard/OverdueBanner";
import { useSettings } from "@/context/SettingsContext";

const TYPES = {
  reminder: { label: "Reminder", icon: Bell, color: "#60a5fa" },
  project: { label: "Project", icon: FolderKanban, color: "#fbbf24" },
  thought: { label: "To discuss", icon: MessageCircleQuestion, color: "#f472b6" },
};

const ReminderMeta = ({ it }) => {
  const overdue = !it.next_fire_at || new Date(it.next_fire_at) <= new Date();
  return (
    <>
      <span className={`font-semibold ${overdue ? "text-amber-300" : "text-blue-300"}`}>{!it.next_fire_at ? "Sent" : overdue ? "Due now" : fromNow(it.when)}</span>
      <span className="font-mono">{fmtDate(it.when)}</span>
      <span className="flex items-center gap-[0.4em] font-semibold" style={{ color: priorityOf(it.priority).color }} data-testid={`wallboard-priority-${it.key}`}>
        <span className="h-[0.6em] w-[0.6em] rounded-full" style={{ background: priorityOf(it.priority).color }} /> {scheduleText(it)}
      </span>
    </>
  );
};

const ProjectMeta = ({ it }) => {
  const d = daysLeft(it);
  return (
    <>
      {it.status === "on_hold" && <span className="font-semibold text-amber-300">On hold</span>}
      {it.end_date && <span className="font-mono">Due {fmtDay(it.end_date)}</span>}
      {d != null && <span className={d < 0 ? "font-semibold text-red-400" : d <= 2 ? "font-semibold text-amber-300" : ""}>{d < 0 ? `${-d}d overdue` : d === 0 ? "due today" : `${d}d left`}</span>}
    </>
  );
};

export const WallCard = forwardRef(({ it, rank, ordering, dragging, style, handleProps, onOpen, ...rest }, ref) => {
  const s = spaceOf(it.space);
  const t = TYPES[it.type];
  const pct = it.type === "project" ? projectProgress(it) : null;
  const overdue = !!overdueSince(it, useSettings()?.team?.reminders?.overdue_minutes || 60);
  const clickable = !ordering && onOpen && it.type === "reminder";
  return (
    <div ref={ref} style={style} {...rest} data-testid={`wallboard-card-${it.key}`}
      onClick={clickable ? () => onOpen(it.id) : undefined} role={clickable ? "button" : undefined}
      className={`relative flex flex-col rounded-[0.9em] border bg-card/80 p-[1.1em] ${clickable ? "cursor-pointer transition-[border-color,transform] hover:-translate-y-0.5 hover:border-white/30" : ""} ${dragging ? "z-50 border-white/40 shadow-2xl shadow-black/60" : ""} ${overdue ? "border-red-500/70 bg-red-500/10 ring-2 ring-red-500/60" : ""} ${ordering ? "cursor-grab touch-none select-none active:cursor-grabbing" : ""}`}
      {...(ordering ? handleProps : {})}>
      <span className="absolute bottom-[0.9em] left-0 top-[0.9em] w-[0.22em] rounded-full" style={{ background: s.color }} />
      <div className="flex items-center gap-[0.6em] text-[0.7em] font-semibold uppercase tracking-wider">
        <span className={`font-mono text-[1.6em] font-bold ${rank <= 3 ? "text-foreground" : "text-muted-foreground"}`} data-testid={`wallboard-rank-${it.key}`}>#{rank}</span>
        <span className="flex items-center gap-1 rounded-full px-[0.7em] py-[0.25em]" style={{ background: `${t.color}1f`, color: t.color }}>
          <t.icon className="h-[1.1em] w-[1.1em]" /> {t.label}
        </span>
        <span className="ml-auto flex items-center gap-[0.4em] normal-case tracking-normal" style={{ color: s.color }}>
          <span className="h-[0.6em] w-[0.6em] rounded-full" style={{ background: s.color }} /> {s.name}
          {it.assignees?.map((a) => <span key={a} className="flex items-center gap-[0.3em]" style={{ color: spaceOf(a).color }} data-testid={`wallboard-assignee-${it.key}-${a}`}>+ {spaceOf(a).name}</span>)}
        </span>
        {ordering && <GripVertical className="h-[1.4em] w-[1.4em] text-muted-foreground" />}
      </div>
      <p className="mt-[0.5em] font-display text-[1.35em] font-semibold leading-tight tracking-tight">{it.title}</p>
      {it.details && <p className="mt-[0.35em] line-clamp-2 text-[0.8em] text-muted-foreground">{it.details}</p>}
      {(it.silent || it.checklist_total > 0) && (
        <div className="mt-[0.4em] flex gap-[0.8em] text-[0.7em] text-muted-foreground">
          {it.checklist_total > 0 && <span className="flex items-center gap-[0.3em]"><ListChecks className="h-[1.1em] w-[1.1em]" /> {it.checklist_done}/{it.checklist_total}</span>}
          {it.silent && <span className="flex items-center gap-[0.3em] text-amber-200/80"><BellOff className="h-[1.1em] w-[1.1em]" /> No emails</span>}
        </div>
      )}
      {it.updates?.length > 0 && (
        <div className="mt-[0.6em] space-y-[0.4em]" data-testid={`wallboard-updates-${it.key}`}>
          {it.updates.map((u) => (
            <div key={u.id} className="border-l-2 pl-[0.6em] text-[0.75em]" style={{ borderColor: spaceOf(u.created_by).color }}>
              <p className="text-muted-foreground"><span className="font-semibold text-foreground">{spaceOf(u.created_by).name}</span> · {fromNow(u.created_at)}</p>
              <p className="line-clamp-2">{u.body}</p>
            </div>
          ))}
          {it.updates_count > it.updates.length && <p className="text-[0.65em] text-muted-foreground">+{it.updates_count - it.updates.length} earlier updates</p>}
        </div>
      )}
      <div className="mt-auto pt-[0.8em]">
        {pct != null && <div className="mb-[0.6em]"><ProgressBar value={pct} color={s.color} /></div>}
        <div className="flex flex-wrap items-center gap-x-[0.9em] gap-y-[0.3em] text-[0.75em] text-muted-foreground">
          {it.type === "reminder" && <ReminderMeta it={it} />}
          {it.type === "project" && <ProjectMeta it={it} />}
          {it.type === "thought" && <span>Raised {fromNow(it.created_at)}</span>}
          {it.project_title && <span className="flex items-center gap-1"><FolderKanban className="h-[1em] w-[1em]" /> {it.project_title}</span>}
        </div>
      </div>
    </div>
  );
});
WallCard.displayName = "WallCard";
